import {
  apiV2DataMonitorAlarmRecordDetailPost,
  V2DataMonitorAlarmRecordDetailPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import {
  BaseInfoContent,
  Button,
  Form,
  FormTitle,
  SubContent,
  Tag,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';
import { Space } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { AlarmLevel, AlarmLevelColorDisplay, AlarmLevelDisplay } from '@/shared/types';

import { DataMonitoringRuleType, DataMonitoringRuleTypeDisplay } from '../DataMonitoringAlarmRules/constants';

const minuteFormat = 'YYYY-MM-DD HH:mm';
const secondFormat = 'YYYY-MM-DD HH:mm:ss';

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== '';

const formatDateTime = (value: string | number | undefined, format: string) =>
  hasValue(value) && dayjs(value).isValid() ? dayjs(value).format(format) : '-';

const DataMonitoringAlarmRecordDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const alarmId = Number(id);
  const hasValidAlarmId = Number.isFinite(alarmId);

  const { data: alarmDetail } = useRequest(() => apiV2DataMonitorAlarmRecordDetailPost({ id: alarmId }), {
    ready: hasValidAlarmId,
    refreshDeps: [alarmId],
  });

  const ruleType = alarmDetail?.ruleType as DataMonitoringRuleType | undefined;
  const isCommunicationStatus = ruleType === DataMonitoringRuleType.COMMUNICATION_STATUS;
  const isDataException = ruleType === DataMonitoringRuleType.DATA_EXCEPTION;
  const isRecovered = alarmDetail?.state === 0;
  const duration = hasValue(alarmDetail?.duration) ? `${alarmDetail?.duration} min` : '-';

  const formatFirstAlarmData = (detail?: V2DataMonitorAlarmRecordDetailPostResponse) => {
    if (!detail?.firstAlarmDataValue) return '-';

    return `${detail.firstAlarmDataValue}${
      hasValue(detail.firstAlarmDataTime) ? `  ${formatDateTime(detail.firstAlarmDataTime, secondFormat)}` : ''
    }`;
  };

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), { name: '查看报警' }]} className="page_wrapper">
      <FormTitle title="查看报警" />
      <SubContent>
        <BaseInfoContent layout="vertical">
          <Form.Item label="规则名称">{alarmDetail?.ruleName ?? '-'}</Form.Item>
          <Form.Item label="报警类型">
            {ruleType === undefined ? '-' : DataMonitoringRuleTypeDisplay[ruleType] ?? '-'}
          </Form.Item>
          <Form.Item label="监听设备">{alarmDetail?.deviceName ?? '-'}</Form.Item>
          <Form.Item label="监测数据属性">{alarmDetail?.dataPropertyName ?? '-'}</Form.Item>
          <Form.Item label="报警状态">
            {alarmDetail?.state === undefined ? '-' : isRecovered ? '已恢复' : '未恢复'}
          </Form.Item>
          <Form.Item label="报警等级">
            {alarmDetail?.alarmLevel === undefined ? (
              '-'
            ) : (
              <Tag border="solid" color={AlarmLevelColorDisplay[alarmDetail.alarmLevel as AlarmLevel]}>
                {AlarmLevelDisplay[alarmDetail.alarmLevel as AlarmLevel]}
              </Tag>
            )}
          </Form.Item>
          <Form.Item label="报警规则编号">
            {alarmDetail?.ruleId && alarmDetail.ruleCode ? (
              <Link
                to={`/device/rule/data-monitoring/${alarmDetail.ruleId}/detail`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {alarmDetail.ruleCode}
              </Link>
            ) : (
              alarmDetail?.ruleCode ?? '-'
            )}
          </Form.Item>
          <Form.Item label="报警时间">{formatDateTime(alarmDetail?.alarmTime, minuteFormat)}</Form.Item>
          <Form.Item label="恢复时间">
            {isRecovered ? formatDateTime(alarmDetail?.recoveryTime, minuteFormat) : '-'}
          </Form.Item>
          <Form.Item label="持续时长">{duration}</Form.Item>

          {isCommunicationStatus && (
            <>
              <Form.Item label="报警前最后一条数据">
                {hasValue(alarmDetail?.lastDataValue) ? alarmDetail?.lastDataValue : '-'}
              </Form.Item>
              <Form.Item label="报警前最后一条数据上传时间">
                {formatDateTime(alarmDetail?.lastDataUploadTime, secondFormat)}
              </Form.Item>
              <Form.Item label="监听时间范围">
                {hasValue(alarmDetail?.monitorTimeRange) ? `${alarmDetail?.monitorTimeRange} min` : '-'}
              </Form.Item>
              <Form.Item label="上传数据个数">
                {hasValue(alarmDetail?.uploadDataCount) ? `>=${alarmDetail?.uploadDataCount}` : '-'}
              </Form.Item>
            </>
          )}

          {isDataException && (
            <>
              <Form.Item label="报警公式">
                <div style={{ wordBreak: 'break-all' }}>{alarmDetail?.alarmFormula ?? '-'}</div>
              </Form.Item>
              <Form.Item label="触发报警第一条数据">{formatFirstAlarmData(alarmDetail)}</Form.Item>
            </>
          )}
        </BaseInfoContent>
      </SubContent>
      <Space className="sticky-footer">
        <Button
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
              return;
            }

            navigate('/device/alarm/data-monitoring');
          }}
        >
          返回
        </Button>
      </Space>
    </Wrapper>
  );
};

export default DataMonitoringAlarmRecordDetail;
